"use client";

import React from "react";
import { Button } from "react-bootstrap";
import { useAuth } from "@/context/auth-context";
import { isDriverUser } from "@/utils/roleUtils";

interface DriverAccessBlockProps {
    children: React.ReactNode;
}

const DriverAccessBlock: React.FC<DriverAccessBlockProps> = ({ children }) => {
    const { userProfile, externalUserProfile, loginType, logout } = useAuth();

    // Determine current profile based on login type
    const currentProfile = React.useMemo(() => {
        return loginType === "msal" ? userProfile : externalUserProfile;
    }, [loginType, userProfile, externalUserProfile]);

    // Check if user is a driver
    const isDriver = React.useMemo(() => {
        return isDriverUser(currentProfile?.roles);
    }, [currentProfile?.roles]);

    // Handle logout
    const handleLogout = React.useCallback(async () => {
        try {
            await logout();
        } catch (error) {
            console.error("Logout error:", error);
            // Fallback to redirect if logout fails
            window.location.href = "/";
        }
    }, [logout]);

    // If user is a driver, show access blocked message
    if (isDriver) {
        return (
            <div
                className="d-flex flex-column justify-content-center align-items-center"
                style={{
                    minHeight: "100vh",
                    backgroundColor: "#f8f9fa",
                    fontFamily:
                        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    padding: "2rem 1rem",
                }}
            >
                <div className="text-center" style={{ maxWidth: "500px" }}>
                    {/* Mobile App Icon */}
                    <div className="mb-4">
                        <svg
                            width="80"
                            height="80"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#007bff"
                            strokeWidth="1.5"
                            className="mx-auto"
                        >
                            <rect
                                x="5"
                                y="2"
                                width="14"
                                height="20"
                                rx="2"
                                ry="2"
                            />
                            <line x1="12" y1="18" x2="12.01" y2="18" />
                        </svg>
                    </div>

                    {/* Heading */}
                    <h1
                        className="mb-3"
                        style={{
                            color: "#007bff",
                            fontSize: "1.8rem",
                            fontWeight: "600",
                            margin: "0 0 1rem 0",
                        }}
                    >
                        Mobile App Access Only
                    </h1>

                    {/* Description */}
                    <p
                        style={{
                            color: "#6c757d",
                            fontSize: "1.1rem",
                            lineHeight: "1.6",
                            margin: "0 0 2rem 0",
                        }}
                    >
                        Hi <strong>{currentProfile?.displayName}</strong>! Your
                        account is configured for mobile app access only. Please
                        use the <strong>DispatchIQ Mobile App</strong> to access
                        your driver dashboard and delivery management features.
                    </p>

                    {/* Features List */}
                    <div
                        className="text-start mb-4 p-3"
                        style={{
                            backgroundColor: "#e3f2fd",
                            borderRadius: "8px",
                            border: "1px solid #bbdefb",
                        }}
                    >
                        <h6 className="mb-2" style={{ color: "#1565c0" }}>
                            Mobile App Features:
                        </h6>
                        <ul
                            style={{
                                color: "#424242",
                                fontSize: "0.95rem",
                                margin: 0,
                            }}
                        >
                            <li>View and manage your delivery assignments</li>
                            <li>Real-time route navigation</li>
                            <li>Update delivery status</li>
                            <li>Capture delivery confirmations</li>
                            <li>Track your daily performance</li>
                        </ul>
                    </div>

                    {/* Action Buttons */}
                    <div className="d-flex gap-3 justify-content-center">
                        <Button
                            variant="outline-secondary"
                            onClick={handleLogout}
                            style={{
                                borderRadius: "8px",
                                padding: "0.75rem 1.5rem",
                                fontWeight: "500",
                                fontSize: "0.95rem",
                                minWidth: "120px",
                            }}
                        >
                            Sign Out
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => {
                                // Open mobile app store or provide download instructions
                                alert(
                                    "Please download the DispatchIQ Mobile App from your device's app store."
                                );
                            }}
                            style={{
                                borderRadius: "8px",
                                padding: "0.75rem 1.5rem",
                                fontWeight: "500",
                                fontSize: "0.95rem",
                                minWidth: "180px",
                            }}
                        >
                            Get Mobile App
                        </Button>
                    </div>

                    {/* Additional Info */}
                    <div className="mt-4">
                        <small
                            style={{ color: "#9e9e9e", fontSize: "0.85rem" }}
                        >
                            Need help? Contact your administrator or IT support.
                        </small>
                    </div>
                </div>
            </div>
        );
    }

    // If user is not a driver, render children normally
    return <>{children}</>;
};

export default DriverAccessBlock;
